import { v4 as uuidv4 } from 'uuid';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { type CaseDetailsPage } from '@/cloudFunctions/arag';
import { getFlattenedMatter } from '@/database/getFlattenedMatter';
import {
  InsuranceCaseDocument,
  MatterDocument,
  PhoneNumber,
} from '@shared/types';
import {
  AddEditMatterFormData,
  FormInsuranceCase,
} from '../AddEditMatter/types';
import { getStateNameFromCode } from '@/optionSets';
import { FlattenedMatter } from '@/database/aggregateTypes';

export interface ExistingAragCaseMatch {
  insuranceCase: InsuranceCaseDocument;
  matter: MatterDocument;
}

export const findExistingAragMattersByCaseAssistNumber = async (props: {
  authSession: AuthenticatedSession;
  caseAssistNumber: string;
}): Promise<ExistingAragCaseMatch[]> => {
  const trimmedCaseAssistNumber = props.caseAssistNumber.trim();
  if (!trimmedCaseAssistNumber) {
    return [];
  }

  const insuranceCasesSnapshot = await props.authSession.db.getInsuranceCases([
    ['provider', '==', 'arag'],
    ['caseId', '==', trimmedCaseAssistNumber],
  ]);

  const matches = await Promise.all(
    insuranceCasesSnapshot.docs.map(async (doc) => {
      const insuranceCase: InsuranceCaseDocument = {
        ...doc.data(),
        id: doc.id,
      };
      if (!insuranceCase.matterId) {
        return undefined;
      }
      const matterSnapshot = await props.authSession.db.getMatter(
        insuranceCase.matterId,
      );
      const matterData = matterSnapshot.data();
      if (!matterData) {
        return undefined;
      }
      const matter: MatterDocument = { ...matterData, id: matterSnapshot.id };
      return {
        insuranceCase,
        matter,
      };
    }),
  );

  const dedupedMatches = new Map<string, ExistingAragCaseMatch>();
  matches.forEach((match) => {
    if (!match) {
      return;
    }
    if (!dedupedMatches.has(match.matter.id)) {
      dedupedMatches.set(match.matter.id, match);
      return;
    }
    const existingMatch = dedupedMatches.get(match.matter.id);
    if (
      existingMatch &&
      existingMatch.insuranceCase.caseId === undefined &&
      match.insuranceCase.caseId !== undefined
    ) {
      dedupedMatches.set(match.matter.id, match);
    }
  });

  return Array.from(dedupedMatches.values());
};

export const convertCaseAssistInfoToAddEditMatterProps = async (props: {
  authSession: AuthenticatedSession;
  caseAssistResult: CaseDetailsPage;
  selectedFeeScheduleIndex: number;
  responsibleAttorneyId: string;
  matter: MatterDocument | undefined;
}): Promise<{
  flattenedMatter: FlattenedMatter | undefined;
  overrides: Partial<AddEditMatterFormData> | undefined;
}> => {
  if (props.matter) {
    const flattenedMatter = await getFlattenedMatter({
      authSession: props.authSession,
      matterId: props.matter.id,
    });
    if (!flattenedMatter) {
      throw new Error('Failed to get flattened matter');
    }

    const insuranceCases: FormInsuranceCase[] =
      convertInsuranceCasesToFormCases(flattenedMatter.insuranceCases);
    insuranceCases.push(
      makeNewFormCase({
        caseAssistResult: props.caseAssistResult,
        caseValue: getSelectedFeeScheduleDetails({
          caseData: props.caseAssistResult,
          selectedFeeScheduleIndex: props.selectedFeeScheduleIndex,
        }).caseValue,
      }),
    );
    const overrides: Partial<AddEditMatterFormData> = {
      insuranceCases,
    };
    return { flattenedMatter, overrides };
  } else {
    const overrides = convertCaseAssistInfoToFormOverrides({
      caseData: props.caseAssistResult,
      responsibleAttorneyId: props.responsibleAttorneyId,
      selectedFeeScheduleIndex: props.selectedFeeScheduleIndex,
    });
    return { flattenedMatter: undefined, overrides };
  }
};

const convertInsuranceCasesToFormCases = (
  insuranceCases: InsuranceCaseDocument[] | undefined,
): FormInsuranceCase[] => {
  if (!insuranceCases) {
    return [];
  }
  return insuranceCases.map((insuranceCase) => ({
    tempId: insuranceCase.id,
    firestoreId: insuranceCase.id,
    caseId: insuranceCase.caseId ?? null,
    memberId: insuranceCase.memberId ?? null,
    caseValue:
      insuranceCase.caseValue !== undefined
        ? insuranceCase.caseValue.toString()
        : null,
    caseType: insuranceCase.caseType ?? null,
    otherInsuranceProviderName:
      insuranceCase.otherInsuranceProviderName ?? null,
  }));
};

const convertCaseAssistInfoToFormOverrides = (props: {
  caseData: CaseDetailsPage;
  responsibleAttorneyId: string;
  selectedFeeScheduleIndex: number;
}): Partial<AddEditMatterFormData> => {
  const { caseValue, description: feeScheduleDescription } =
    getSelectedFeeScheduleDetails({
      caseData: props.caseData,
      selectedFeeScheduleIndex: props.selectedFeeScheduleIndex,
    });

  const insuranceCase = makeNewFormCase({
    caseAssistResult: props.caseData,
    caseValue,
  });

  const nameParts = props.caseData.memberName?.split(' ');
  const firstName = nameParts?.slice(0, -1).join(' ');
  const lastName = nameParts?.[nameParts.length - 1];
  const phoneNumbers: PhoneNumber[] = [];
  let primaryPhoneNumberId: string | null = null;
  if (props.caseData.memberPhone) {
    const phoneNumberId = uuidv4();
    phoneNumbers.push({
      id: phoneNumberId,
      value: props.caseData.memberPhone,
    });
    primaryPhoneNumberId = phoneNumberId;
  }
  // Example: "New York, NY 10001"
  const stateCodeMatch =
    props.caseData.memberCityStateZip?.match(/,\s*([A-Za-z]{2})\b/);
  const stateCode = stateCodeMatch?.[1];
  const stateName = getStateNameFromCode(stateCode ?? '');

  const result: Partial<AddEditMatterFormData> = {
    feeType: 'Insurance',
    description: `ARAG ${props.caseData.typeOfLegalMatter} for ${
      props.caseData.memberName
    } ${feeScheduleDescription ? `\n\n${feeScheduleDescription}` : ''}`,
    scope: props.caseData.typeOfLegalMatter,
    responsibleAttorneyId: props.responsibleAttorneyId,
    insuranceProvider: 'arag',
    insuranceCases: [insuranceCase],
    state: stateName,
    practiceAreaId: null,
    otherAssigneeId: null,
    clients: [
      {
        tempId: uuidv4(),
        firestoreId: null,
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        phoneNumbers,
        primaryPhoneNumberId,
        emailAddresses: [],
        primaryEmailAddressId: null,
        role: 'client',
        type: 'human',
        preferredLanguage: null,
      },
    ],
  };
  return result;
};

const getSelectedFeeScheduleDetails = (props: {
  caseData: CaseDetailsPage;
  selectedFeeScheduleIndex: number;
}): { caseValue: number | undefined; description: string | undefined } => {
  const { caseData, selectedFeeScheduleIndex } = props;
  const selectedFee = caseData.feeSchedule?.[selectedFeeScheduleIndex];
  if (!selectedFee) {
    return { caseValue: undefined, description: undefined };
  }
  const rateMatch = selectedFee.rate.match(/[\d,]+\.?\d*/);
  const caseValue = rateMatch
    ? parseFloat(rateMatch[0].replace(/,/g, ''))
    : undefined;
  return { caseValue, description: selectedFee.description };
};

const makeNewFormCase = (props: {
  caseAssistResult: CaseDetailsPage;
  caseValue: number | undefined;
}): FormInsuranceCase => {
  const { caseAssistResult, caseValue } = props;
  return {
    tempId: uuidv4(),
    firestoreId: null,
    caseId: caseAssistResult.caseAssistNumber,
    memberId: caseAssistResult.memberId ?? null,
    caseValue: caseValue !== undefined ? caseValue.toString() : null,
    caseType: caseAssistResult.typeOfLegalMatter ?? null,
    otherInsuranceProviderName: null,
  };
};
